import { useState } from 'react'
import { FieldValues, useFormContext } from 'react-hook-form'
import { FormControl, FormField, FormItem, FormLabel } from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Button } from '@/common/ui/button'
import { Separator } from '@/common/ui/separator'
import { TriangleAlert } from 'lucide-react'
import { PresetConfig, PresetConfigFieldsProps } from '../types'
import useQueryGetPresetId from '@/modules/promotion/api/hooks/useQueryGetPresetId'
import { RtpPresetInfoTooltip } from '@/modules/promotion/components/RtpPresetInfoTooltip'
import { PresetPriorityHint } from '@/common/components/PresetPriorityHint'

export function PresetConfigFields<
	TFieldValues extends FieldValues = FieldValues,
>({
	control,
	fieldPrefix = '',
	turnoverRound,
}: PresetConfigFieldsProps<TFieldValues>) {
	const getFieldName = (field: string) =>
		fieldPrefix ? `${fieldPrefix}.${field}` : field

	const { data: presets } = useQueryGetPresetId()
	const {
		formState: { errors },
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} = useFormContext() as any

	const getFieldErrors = (index: number, subField: string) => {
		const parts = fieldPrefix ? fieldPrefix.split('.') : []
		// Navigate errors object: e.g. errors.walletCondition.presetConfigs[0].presetId
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let node: any = errors
		for (const part of parts) {
			node = node?.[part]
		}
		return node?.presetConfigs?.[index]?.[subField]?.message as
			| string
			| undefined
	}

	const getArrayError = () => {
		const parts = fieldPrefix ? fieldPrefix.split('.') : []
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let node: any = errors
		for (const part of parts) {
			node = node?.[part]
		}
		return node?.presetConfigs?.message as string | undefined
	}

	const [globalThresholdType, setGlobalThresholdType] = useState<
		'FIXED' | 'MULTIPLIER'
	>('FIXED')
	const isGlobalMultiplier = globalThresholdType === 'MULTIPLIER'

	return (
		<>
			<Separator />
			<FormField
				control={control}
				name={getFieldName('presetConfigs') as any}
				render={({ field }) => {
					return (
						<FormItem>
							<div className="flex items-center gap-2">
								<FormLabel className="text-sm text-foreground sm:text-base">
									ตั้งค่า RTP Preset
								</FormLabel>
								<RtpPresetInfoTooltip />
							</div>
							<PresetPriorityHint where="promotion" className="mt-2 w-full" />
							<div className="mb-2 text-sm text-muted-foreground">
								หมายเหตุ: ระบบมี 2 Preset ที่สามารถตั้งค่าได้
							</div>

							{/* Global Threshold Type Toggle */}
							<div className="mb-4">
								<FormLabel className="text-sm text-foreground sm:text-base">
									ประเภทเงื่อนไข
								</FormLabel>
								<div className="mt-1 flex gap-2">
									<Button
										type="button"
										size="sm"
										variant={!isGlobalMultiplier ? 'default' : 'outline'}
										onClick={() => {
											setGlobalThresholdType('FIXED')
											field.onChange(
												((field.value || []) as PresetConfig[]).map((c) => ({
													...c,
													thresholdType: 'FIXED' as const,
													turnoverMultiplier: null,
												})),
											)
										}}
									>
										คงที่ (เครดิต)
									</Button>
									<Button
										type="button"
										size="sm"
										variant={isGlobalMultiplier ? 'default' : 'outline'}
										onClick={() => {
											setGlobalThresholdType('MULTIPLIER')
											field.onChange(
												((field.value || []) as PresetConfig[]).map((c) => ({
													...c,
													thresholdType: 'MULTIPLIER' as const,
													turnoverThreshold: null,
												})),
											)
										}}
									>
										เท่าของยอดฝากรวมโบนัส (x)
									</Button>
								</div>
								<p className="mt-2 text-xs text-muted-foreground">
									หมายเหตุ: ระบุจำนวนเทิร์นโอเวอร์เป็นตัวเลขเครดิต (เช่น
									เมื่อผู้เล่นทำเทิร์นครบ 500 เครดิต) หรือ
									ระบุจำนวนเทิร์นโอเวอร์เป็นจำนวนเท่าของยอดฝากรวมโบนัส (เช่น
									เมื่อผู้เล่นทำเทิร์นครบ 1 เท่า)
								</p>
							</div>

							<FormControl>
								<div className="space-y-6">
									{field.value?.map((config: PresetConfig, index: number) => {
										const isMultiplier = isGlobalMultiplier
										const multiplierExceedsRound =
											isMultiplier &&
											turnoverRound != null &&
											config.turnoverMultiplier != null &&
											config.turnoverMultiplier >= turnoverRound

										return (
											<div
												key={index}
												className="space-y-4 rounded-lg border p-4"
											>
												<div className="flex items-center justify-between">
													<h3 className="text-base font-semibold">
														Preset {index + 1}
													</h3>
													<Button
														type="button"
														variant="destructive"
														size="sm"
														onClick={() => {
															const newValue = field.value?.filter(
																(_: PresetConfig, i: number) => i !== index,
															)
															field.onChange(newValue)
														}}
													>
														ลบ
													</Button>
												</div>

												{/* Turnover Threshold (FIXED) */}
												{!isMultiplier && (
													<div className="grid grid-cols-1 gap-4">
														<FormItem>
															<FormLabel className="text-sm text-foreground sm:text-base">
																จำนวนเทิร์นโอเวอร์ที่จะเริ่มใช้งาน RTP Preset
																นี้ (เครดิต)
															</FormLabel>
															<FormControl>
																<Input
																	type="number"
																	min={0}
																	placeholder="จำนวนเทิร์นโอเวอร์"
																	value={config.turnoverThreshold ?? 0}
																	onChange={(e) => {
																		const newValue = [
																			...(field.value || []),
																		] as PresetConfig[]
																		newValue[index] = {
																			...newValue[index],
																			turnoverThreshold: e.target.value
																				? Number(e.target.value)
																				: 0,
																		}
																		field.onChange(newValue)
																	}}
																	className="text-sm sm:text-base"
																/>
															</FormControl>
														</FormItem>
													</div>
												)}

												{/* Turnover Multiplier (MULTIPLIER) */}
												{isMultiplier && (
													<div className="grid grid-cols-1 gap-4">
														<FormItem>
															<FormLabel className="text-sm text-foreground sm:text-base">
																จำนวนเท่าของยอดฝากรวมโบนัส
																{turnoverRound != null && (
																	<span className="ml-1 text-xs font-normal text-muted-foreground">
																		(ต้องน้อยกว่า {turnoverRound}x)
																	</span>
																)}
															</FormLabel>
															<FormControl>
																<Input
																	type="number"
																	min={0}
																	step={0.5}
																	placeholder="เช่น 1, 2, 3"
																	value={config.turnoverMultiplier ?? 0}
																	onChange={(e) => {
																		const newValue = [
																			...(field.value || []),
																		] as PresetConfig[]
																		newValue[index] = {
																			...newValue[index],
																			turnoverMultiplier: e.target.value
																				? Number(e.target.value)
																				: 0,
																		}
																		field.onChange(newValue)
																	}}
																	className="text-sm sm:text-base"
																/>
															</FormControl>
															{multiplierExceedsRound && (
																<div className="mt-1 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs dark:border-amber-800 dark:bg-amber-950/30">
																	<TriangleAlert className="mt-0.5 h-3 w-3 shrink-0 text-amber-600 dark:text-amber-400" />
																	<span className="text-amber-800 dark:text-amber-200">
																		ค่า {config.turnoverMultiplier}x มากกว่า
																		เทิร์นโอเวอร์ของโปรโมชั่น ({turnoverRound}
																		x) หากเทิร์นโอเวอร์ถึงค่านี้แล้ว
																		ผู้เล่นจะสำเร็จและ RTP Preset
																		นี้จะไม่ถูกใช้งาน
																	</span>
																</div>
															)}
														</FormItem>
													</div>
												)}

												{/* RTP Preset Dropdown (PG/Joker) */}
												<div className="rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950/30">
													<p className="mb-2 text-xs font-medium text-blue-700 dark:text-blue-300">
														🎮 ใช้กับ: PGHARD, JOKERHARD
													</p>
													<p className="mb-3 text-xs text-blue-600 dark:text-blue-400">
														ค่านี้จะถูกใช้เมื่อผู้เล่นเล่นเกมจากผู้ให้บริการ
														PGHARD หรือ JOKERHARD เท่านั้น
													</p>
													<FormItem>
														<FormLabel className="text-sm text-foreground sm:text-base">
															เลือก RTP Preset
														</FormLabel>
														<FormControl>
															<Select
																value={config.presetId ?? undefined}
																onValueChange={(value) => {
																	const newValue = [
																		...(field.value || []),
																	] as PresetConfig[]
																	newValue[index] = {
																		...newValue[index],
																		presetId: value,
																	}
																	field.onChange(newValue)
																}}
															>
																<SelectTrigger className="text-sm sm:text-base">
																	<SelectValue placeholder="เลือก RTP Preset" />
																</SelectTrigger>
																<SelectContent>
																	{presets?.presets.map((preset) => (
																		<SelectItem
																			key={preset.id}
																			value={preset.id}
																		>
																			{preset.note}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
														</FormControl>
														{getFieldErrors(index, 'presetId') && (
															<p className="text-sm font-medium text-destructive">
																{getFieldErrors(index, 'presetId')}
															</p>
														)}
													</FormItem>
												</div>

												{/* RTP Percentage Number (HardV2) */}
												<div className="rounded-md border border-purple-200 bg-purple-50 p-3 dark:border-purple-800 dark:bg-purple-950/30">
													<p className="mb-2 text-xs font-medium text-purple-700 dark:text-purple-300">
														🎮 ใช้กับ: ค่ายปรับแตกอื่นๆทั้งหมด
													</p>
													<p className="mb-3 text-xs text-purple-600 dark:text-purple-400">
														ค่านี้จะถูกใช้เมื่อผู้เล่นเล่นเกมจากผู้ให้บริการ
														JILIHARD, PPHARD, NLCHARD และค่ายอื่นๆทั้งหมด
													</p>
													<FormItem>
														<FormLabel className="text-sm text-foreground sm:text-base">
															เปอร์เซ็น RTP (30-99)
														</FormLabel>
														<FormControl>
															<Input
																type="number"
																min={30}
																max={99}
																placeholder="ระบุเปอร์เซ็น RTP (30-99)"
																value={config.presetNumber ?? ''}
																onChange={(e) => {
																	const newValue = [
																		...(field.value || []),
																	] as PresetConfig[]
																	newValue[index] = {
																		...newValue[index],
																		presetNumber: e.target.value
																			? Number(e.target.value)
																			: null,
																	}
																	field.onChange(newValue)
																}}
																className="text-sm sm:text-base"
															/>
														</FormControl>
														{getFieldErrors(index, 'presetNumber') && (
															<p className="text-sm font-medium text-destructive">
																{getFieldErrors(index, 'presetNumber')}
															</p>
														)}
													</FormItem>
												</div>
											</div>
										)
									})}
									<Button
										type="button"
										variant="outline"
										size="sm"
										className="w-full sm:w-auto"
										onClick={() => {
											const newValue = [
												...(field.value || []),
												{
													thresholdType: globalThresholdType,
													turnoverThreshold:
														globalThresholdType === 'FIXED' ? 0 : null,
													turnoverMultiplier:
														globalThresholdType === 'MULTIPLIER' ? 0 : null,
													presetId: presets?.presets[0]?.id,
													presetNumber: undefined,
													order: field.value?.length || 0,
												},
											]
											field.onChange(newValue)
										}}
									>
										เพิ่ม RTP Preset
									</Button>
								</div>
							</FormControl>
							{getArrayError() && (
								<p className="text-sm font-medium text-destructive">
									{getArrayError()}
								</p>
							)}
						</FormItem>
					)
				}}
			/>
		</>
	)
}
